/** Name of enum in PSQL */
export const DermloopUserRolePsqlEnumName = 'dermloop_user_role_enum';

export enum DermloopUserRoleEnum {
  LearnUser = 'LearnUser', // Users with access to learn
  Manager = 'Manager', // Intended for executive users and all developers
  MedicalUnitManager = 'MedicalUnitManager', // Executive users of a medical unit, sort of like a medical unit admin
  MedicalUnitParticipant = 'MedicalUnitParticipant', // User that is part of any medical unit
  MedicalUnitAssistant = 'MedicalUnitAssistant', // Medical unit role for assistants (nurses, secretaries) — can create drafts but cannot submit/complete triages
  Editor = 'Editor', // Users that have access to editing training conditions
  Reviewer = 'Reviewer', // Intended for users with access to view all training conditions and propose changes that must be approved by managers or possibly editors
  Admin = 'Admin', // Intended for select developers
  MedicalUnitPathologist = 'MedicalUnitPathologist', // Medical unit role specifically for pathologists
  MedicalUnitSpecialist = 'MedicalUnitSpecialist', // Medical unit role specifically for specialists
  MedicalUnitSpecialistManager = 'MedicalUnitSpecialistManager', // Additional role for specialists managing other specialists. Have access to more cases. Needs to be applied together with MedicalUnitSpecialist
  MedicalUnitTrainingAssessmentExporter = 'MedicalUnitTrainingAssessmentExporter', // Role for exporting training assessments as csv for a medical unit
  JurisdictionalGroupInsuranceManager = 'JurisdictionalGroupInsuranceManager', // Jurisdictional group insurance manager
  SiteManager = 'SiteManager', // Site manager, allows for administrating all medical units in a medical unit group
  FederationManager = 'FederationManager', // Allows the user to administrate multiple jurisdictional groups, medical units and its users.
  Researcher = 'Researcher', // A role that gives access to niche and small specific things only for researchers or before releasing a feature to the public
}

/**
 * Global roles that are not affiliated with a medical unit, jurisdictional group or federation.
 */
export const NotAffiliatedRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.LearnUser,
  DermloopUserRoleEnum.Admin,
  DermloopUserRoleEnum.Manager,
  DermloopUserRoleEnum.Editor,
  DermloopUserRoleEnum.Reviewer,
];

/**
 * Roles exempt from the 90-day inactivity deactivation.
 *
 * Must stay non-empty: this list is expanded into SQL `IN (...)` clauses
 * (user-affiliation.service spread param, user.service `.map().join()`),
 * and an empty list emits invalid `IN ()` SQL that throws at runtime.
 */
export const INACTIVITY_DEACTIVATION_EXEMPT_ROLES: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.SiteManager,
  DermloopUserRoleEnum.MedicalUnitManager,
];

/**
 * Roles that do not expire. Used when creating user affiliations.
 */
export const NotExpiringRoles: DermloopUserRoleEnum[] = [
  ...NotAffiliatedRoles,
  DermloopUserRoleEnum.MedicalUnitManager,
  DermloopUserRoleEnum.SiteManager,
  DermloopUserRoleEnum.FederationManager,
  DermloopUserRoleEnum.MedicalUnitSpecialistManager,
  DermloopUserRoleEnum.JurisdictionalGroupInsuranceManager,
];

/**
 * Remember to add the role to the "getRoleRequirements" function in add-user-role-drawer.tsx
 * Other wise very bad.
 */
export const DermloopMedicalUnitRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.MedicalUnitParticipant,
  DermloopUserRoleEnum.MedicalUnitAssistant,
  DermloopUserRoleEnum.MedicalUnitManager,
  DermloopUserRoleEnum.MedicalUnitPathologist,
  DermloopUserRoleEnum.MedicalUnitSpecialist,
  DermloopUserRoleEnum.MedicalUnitSpecialistManager,
  DermloopUserRoleEnum.MedicalUnitTrainingAssessmentExporter,
  DermloopUserRoleEnum.Researcher,
  DermloopUserRoleEnum.SiteManager,
  DermloopUserRoleEnum.JurisdictionalGroupInsuranceManager,
];

/**
 * Roles that make an affiliation eligible to be a specialist-group member: the base
 * specialist role and its manager variant. Every place that decides who can join or
 * be added to a specialist group must use this same set.
 */
export const MedicalUnitSpecialistRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.MedicalUnitSpecialist,
  DermloopUserRoleEnum.MedicalUnitSpecialistManager,
];

/**
 * Melatech-internal roles. They see the internal-only parts of the desktop
 * administration panel — raw entity IDs, user merge, tele assignment — which
 * are hidden from customer administrators.
 *
 * Not a general privilege level: unit-scoped endpoints stay out of reach,
 * because `MedicalUnitRolesGuard` matches only roles bound to the requested
 * medical unit and neither role is affiliated with one.
 */
export const DermloopDadminRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.Admin,
  DermloopUserRoleEnum.Manager,
];

/** Every role that administers users somewhere, customer-facing ones included. */
export const DermloopAdministratorRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.Admin,
  DermloopUserRoleEnum.Manager,
  DermloopUserRoleEnum.MedicalUnitManager,
  DermloopUserRoleEnum.SiteManager,
  DermloopUserRoleEnum.JurisdictionalGroupInsuranceManager,
  DermloopUserRoleEnum.FederationManager,
];

export type DermloopMedicalUnitRole =
  | (typeof DermloopMedicalUnitRoles)[number]
  | DermloopUserRoleEnum.JurisdictionalGroupInsuranceManager;

/**
 * Additional user roles, that end users cannot assign to users.
 * Used for displaying user roles in the UI to end users that are
 * considered "special".
 */
export const AdditionalMedicalUnitRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.MedicalUnitManager,
  DermloopUserRoleEnum.MedicalUnitAssistant,
  DermloopUserRoleEnum.MedicalUnitPathologist,
  DermloopUserRoleEnum.MedicalUnitSpecialist,
];

export const DermloopInvitationRoles: DermloopUserRoleEnum[] = [
  DermloopUserRoleEnum.MedicalUnitParticipant,
  DermloopUserRoleEnum.MedicalUnitAssistant,
  DermloopUserRoleEnum.MedicalUnitSpecialist,
];

/**
 * Pairs of roles that are mutually exclusive — a user affiliation cannot have both
 * roles from any pair at the same time. Add new pairs here as needed.
 */
export const MutuallyExclusiveRolePairs: [
  DermloopUserRoleEnum,
  DermloopUserRoleEnum,
][] = [
  [
    DermloopUserRoleEnum.MedicalUnitAssistant,
    DermloopUserRoleEnum.MedicalUnitParticipant,
  ],
];

export enum DermloopRoleStatus {
  Active = 'Active',
  Deactivated = 'Deactivated',
  AboutToDeactivate = 'AboutToDeactivate',
  /**
   * Never belonged to a medical unit, jurisdictional group or federation — the
   * placeholder affiliation every account gets on creation does not count. One
   * whose real affiliation was removed is Deactivated instead.
   */
  Unaffiliated = 'Unaffiliated',
}
